import React, { useRef, useEffect, useState, memo } from "react";

interface VideoGalleryItemProps {
  src: string;
  alt: string;
  pauseWhenPopupOpen?: boolean;
  onLoadedData?: () => void;
  onPlaying?: () => void;
}

const VideoGalleryItem: React.FC<VideoGalleryItemProps> = memo(({ 
  src, 
  alt, 
  pauseWhenPopupOpen = false,
  onLoadedData, 
  onPlaying 
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
        
        // Pause other videos when this one starts playing
        if (entry.isIntersecting && videoRef.current) {
          const allVideos = document.querySelectorAll('video');
          allVideos.forEach(video => {
            if (video !== videoRef.current && !video.paused) {
              video.pause();
            }
          });
        }
      },
      {
        threshold: 0.5, // Video starts playing when 50% is visible (better for mobile)
        rootMargin: '0px', // No preloading to save resources
      }
    );

    if (videoRef.current) {
      observer.observe(videoRef.current);
    }

    return () => {
      if (videoRef.current) {
        observer.unobserve(videoRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (videoRef.current) {
      // Pausar vídeo se popup estiver aberto ou se não estiver em view
      if (pauseWhenPopupOpen || !isInView) {
        videoRef.current.pause();
      } else if (isInView && !pauseWhenPopupOpen) {
        videoRef.current.play().catch(() => {
          // Handle autoplay failure silently
        });
      }
    }
  }, [isInView, pauseWhenPopupOpen]);

  return (
    <div 
      className="relative group cursor-pointer overflow-hidden rounded-lg"
      style={{ aspectRatio: '9/16' }}
    >
      <video
        ref={videoRef}
        src={src}
        muted
        loop
        playsInline
        preload="metadata"
        onLoadedData={onLoadedData}
        onPlaying={onPlaying}
        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        aria-label={alt}
      />
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison - only re-render if actual data props change
  return (
    prevProps.src === nextProps.src &&
    prevProps.pauseWhenPopupOpen === nextProps.pauseWhenPopupOpen
    // Ignoring onLoadedData and onPlaying as they are stable callbacks
  );
});

VideoGalleryItem.displayName = "VideoGalleryItem";

export default VideoGalleryItem;